import { useUser } from '../contexts/UserContext';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessRoute,
  isAdminUser,
  hasAdminRole,
  isViewer,
  hasViewerRole,
  canViewResources,
  canManageResources,
  PERMISSIONS,
} from '../utils/permissions';

/**
 * Hook for checking user permissions in components
 * Automatically handles admin role bypass
 */
export function usePermissions() {
  const { permissions, activeRole, userInfo } = useUser();

  // Get all roles from userInfo
  const allRoles = userInfo?.roles || [];

  return {
    // Check single permission
    can: (policyRef: string) => hasPermission(permissions, policyRef, activeRole, allRoles),
    
    // Check if user has any of the permissions
    canAny: (policyRefs: string[]) => hasAnyPermission(permissions, policyRefs, activeRole, allRoles),
    
    // Check if user has all permissions
    canAll: (policyRefs: string[]) => hasAllPermissions(permissions, policyRefs, activeRole, allRoles),
    
    // Check route access
    // Honours the backend's is_admin / is_delegated_admin, as isAdmin() does.
    canAccessRoute: (route: string) => canAccessRoute(route, permissions, activeRole, allRoles, userInfo),
    
    // Check if user is admin: the backend's explicit flags, then the active or
    // any held role by its is_admin flag or exact admin key/name (never a
    // substring of the name).
    isAdmin: () => isAdminUser(userInfo, activeRole, allRoles),
    
    // Check if user has any admin role
    hasAnyAdminRole: () => hasAdminRole(allRoles),
    
    // Check if user is viewer
    isViewer: () => isViewer(activeRole),
    
    // Check if user has any viewer role
    hasAnyViewerRole: () => hasViewerRole(allRoles),
    
    // Check if user can view resources (admin or viewer)
    canViewResources: () => canViewResources(activeRole, allRoles),
    
    // Check if user can manage resources (admin only)
    canManageResources: () => canManageResources(activeRole, allRoles),
    
    // Expose permissions constant for easy access
    PERMISSIONS,
  };
}
